/**
 * Carousel Layout Module
 * Horizontal scrolling carousel with touch/swipe support
 */

/**
 * Carousel layout renderer
 */
export class CarouselLayout {
  /**
   * Creates a carousel layout instance
   * @param {Object} config - Layout configuration
   */
  constructor(config = {}) {
    this.itemWidth = config.itemWidth || 300;
    this.gap = config.gap || 16;
    this.showArrows = config.showArrows !== false;
    this.autoScroll = config.autoScroll || false;
    this.autoScrollDelay = config.autoScrollDelay || 5000;
    this.currentScroll = 0;
    this.autoScrollInterval = null;
  }

  /**
   * Renders the carousel layout
   * @param {Array} posts - Instagram posts data
   * @param {HTMLElement} container - Container element
   * @returns {HTMLElement} Rendered carousel element
   */
  render(posts, container) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ig-widget-carousel-wrapper';
    wrapper.style.cssText = `
      position: relative;
      width: 100%;
      overflow: hidden;
    `;

    const carousel = document.createElement('div');
    carousel.className = 'ig-widget-carousel';
    carousel.style.cssText = `
      display: flex;
      gap: ${this.gap}px;
      overflow-x: auto;
      scroll-behavior: smooth;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding: 8px 0;
    `;

    // Hide scrollbar
    const style = document.createElement('style');
    style.textContent = `
      .ig-widget-carousel::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);

    posts.forEach(post => {
      const item = this.createCarouselItem(post);
      carousel.appendChild(item);
    });

    wrapper.appendChild(carousel);

    // Add navigation arrows if enabled
    if (this.showArrows && posts.length > 1) {
      this.addNavigationArrows(wrapper, carousel);
    }

    // Add touch/swipe support
    this.addSwipeSupport(carousel);

    // Add auto-scroll if enabled
    if (this.autoScroll) {
      this.startAutoScroll(carousel);
    }

    return wrapper;
  }

  /**
   * Creates a single carousel item
   * @param {Object} post - Instagram post data
   * @returns {HTMLElement} Carousel item element
   */
  createCarouselItem(post) {
    const item = document.createElement('a');
    item.className = 'ig-widget-carousel-item';
    item.href = post.permalink;
    item.target = '_blank';
    item.rel = 'noopener noreferrer';
    item.setAttribute('aria-label', post.caption || 'Instagram post');

    item.style.cssText = `
      flex: 0 0 ${this.itemWidth}px;
      scroll-snap-align: start;
      text-decoration: none;
      color: inherit;
    `;

    const mediaUrl = post.media_type === 'VIDEO'
      ? (post.thumbnail_url || post.media_url)
      : post.media_url;

    item.innerHTML = `
      <div class="ig-widget-carousel-item-inner" style="
        position: relative;
        width: 100%;
        aspect-ratio: 4/5;
        overflow: hidden;
        border-radius: 8px;
        background: var(--ig-widget-bg-secondary, #f5f5f5);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
      ">
        <img
          src="${mediaUrl}"
          alt="${this.escapeHtml(post.caption || 'Instagram post')}"
          loading="lazy"
          style="
            width: 100%;
            height: 100%;
            object-fit: cover;
          "
        />
        ${post.media_type === 'VIDEO' ? '<div class="ig-widget-video-icon" style="position: absolute; top: 12px; right: 12px; color: white; font-size: 24px; text-shadow: 0 2px 4px rgba(0,0,0,0.5);">▶</div>' : ''}
        ${post.media_type === 'CAROUSEL_ALBUM' ? '<div class="ig-widget-carousel-icon" style="position: absolute; top: 12px; right: 12px; color: white; font-size: 24px; text-shadow: 0 2px 4px rgba(0,0,0,0.5);">⋮⋯</div>' : ''}
      </div>
      ${post.caption ? `<div class="ig-widget-carousel-caption" style="
        margin-top: 8px;
        font-size: 14px;
        line-height: 1.4;
        color: var(--ig-widget-text-secondary, #666);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      ">${this.escapeHtml(post.caption)}</div>` : ''}
    `;

    // Add hover effect
    item.addEventListener('mouseenter', () => {
      const inner = item.querySelector('.ig-widget-carousel-item-inner');
      if (inner) {
        inner.style.transform = 'translateY(-4px)';
        inner.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
      }
    });

    item.addEventListener('mouseleave', () => {
      const inner = item.querySelector('.ig-widget-carousel-item-inner');
      if (inner) {
        inner.style.transform = 'translateY(0)';
        inner.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      }
    });

    return item;
  }

  /**
   * Adds navigation arrows to carousel
   * @param {HTMLElement} wrapper - Carousel wrapper
   * @param {HTMLElement} carousel - Carousel element
   */
  addNavigationArrows(wrapper, carousel) {
    const arrowStyle = `
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.9);
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: background 0.3s ease, transform 0.3s ease;
      z-index: 10;
    `;

    const prevArrow = document.createElement('button');
    prevArrow.className = 'ig-widget-carousel-prev';
    prevArrow.innerHTML = '←';
    prevArrow.setAttribute('aria-label', 'Previous');
    prevArrow.style.cssText = arrowStyle + 'left: 16px;';

    const nextArrow = document.createElement('button');
    nextArrow.className = 'ig-widget-carousel-next';
    nextArrow.innerHTML = '→';
    nextArrow.setAttribute('aria-label', 'Next');
    nextArrow.style.cssText = arrowStyle + 'right: 16px;';

    // Add hover effects
    [prevArrow, nextArrow].forEach(arrow => {
      arrow.addEventListener('mouseenter', () => {
        arrow.style.background = 'rgba(255, 255, 255, 1)';
        arrow.style.transform = 'translateY(-50%) scale(1.1)';
      });
      arrow.addEventListener('mouseleave', () => {
        arrow.style.background = 'rgba(255, 255, 255, 0.9)';
        arrow.style.transform = 'translateY(-50%) scale(1)';
      });
    });

    // Add click handlers
    prevArrow.addEventListener('click', () => {
      this.scroll(carousel, -1);
    });

    nextArrow.addEventListener('click', () => {
      this.scroll(carousel, 1);
    });

    wrapper.appendChild(prevArrow);
    wrapper.appendChild(nextArrow);
  }

  /**
   * Scrolls the carousel
   * @param {HTMLElement} carousel - Carousel element
   * @param {number} direction - Direction (-1 for left, 1 for right)
   */
  scroll(carousel, direction) {
    const scrollAmount = this.itemWidth + this.gap;
    carousel.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }

  /**
   * Adds touch/swipe support
   * @param {HTMLElement} carousel - Carousel element
   */
  addSwipeSupport(carousel) {
    let startX = 0;
    let scrollLeft = 0;
    let isDown = false;

    carousel.addEventListener('mousedown', (e) => {
      isDown = true;
      carousel.style.cursor = 'grabbing';
      startX = e.pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
    });

    carousel.addEventListener('mouseleave', () => {
      isDown = false;
      carousel.style.cursor = 'grab';
    });

    carousel.addEventListener('mouseup', () => {
      isDown = false;
      carousel.style.cursor = 'grab';
    });

    carousel.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 2;
      carousel.scrollLeft = scrollLeft - walk;
    });

    // Touch support
    carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].pageX - carousel.offsetLeft;
      scrollLeft = carousel.scrollLeft;
    });

    carousel.addEventListener('touchmove', (e) => {
      const x = e.touches[0].pageX - carousel.offsetLeft;
      const walk = (x - startX) * 2;
      carousel.scrollLeft = scrollLeft - walk;
    });

    carousel.style.cursor = 'grab';
  }

  /**
   * Starts auto-scroll
   * @param {HTMLElement} carousel - Carousel element
   */
  startAutoScroll(carousel) {
    this.autoScrollInterval = setInterval(() => {
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;

      if (carousel.scrollLeft >= maxScroll) {
        carousel.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.scroll(carousel, 1);
      }
    }, this.autoScrollDelay);

    // Pause on hover
    carousel.addEventListener('mouseenter', () => {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
      }
    });

    carousel.addEventListener('mouseleave', () => {
      this.startAutoScroll(carousel);
    });
  }

  /**
   * Stops auto-scroll
   */
  stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Cleanup method
   */
  destroy() {
    this.stopAutoScroll();
  }
}

export default CarouselLayout;
